import CortanaComposeResultComponent from './CortanaComposeResultComponent';
import cortanaComposeStore from '../store/cortanaComposeStore';
import { CortanaComposeViewState } from '../store/schema/CortanaComposeViewState';
import { observer } from 'mobx-react';
import { Callout, DirectionalHint } from 'office-ui-fabric-react/lib/Callout';
import { getRTL } from 'office-ui-fabric-react/lib/Utilities';
import { IPoint } from 'office-ui-fabric-react/lib/Utilities';
import { getIconForFile } from 'owa-file-icon';
import strings from 'owa-strings/owa-cortana-strings';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import CortanaComposeResult, {
    CortanaComposeResultType,
} from '../store/schema/CortanaComposeResult';

const styles = require('./InlineSearchResult.scss');
const classNames = require('classnames/bind').bind(styles);

export interface InlineSearchPopupProps {
    target: IPoint;
    bufferZone: number;
    viewState: CortanaComposeViewState;
}

@observer
export default class InlineSearchPopup extends React.Component<InlineSearchPopupProps, any> {
    render() {
        const { viewState, target, bufferZone } = this.props;
        const sectionNames = [...viewState.resultsShown.keys()];

        return viewState.isPopupShown ? (
            <Callout
                className={styles.callout}
                gapSpace={bufferZone}
                target={target}
                isBeakVisible={false}
                setInitialFocus={false}
                directionalHint={
                    getRTL() ? DirectionalHint.bottomRightEdge : DirectionalHint.bottomLeftEdge
                }>
                {sectionNames.length != 0 && (
                    <div className={classNames(styles.scrollContainer, 'customScrollBar')}>
                        <div className={styles.searchResultsCollection}>
                            {sectionNames.reduce((res, sectionName, currentIndex) => {
                                res = res.concat(
                                    renderSection(
                                        viewState,
                                        viewState.resultsShown.get(sectionName),
                                        currentIndex === viewState.selectedSectionIndex
                                    )
                                );
                                return res;
                            }, [])}
                            {renderInfoText()}
                        </div>
                    </div>
                )}
            </Callout>
        ) : null;
    }
}

function renderInfoText(): JSX.Element {
    // NOTE: this string is just for SDF release. Will be removed before shipping beyond SDF
    return (
        <div className={styles.infoText}>
            {
                'Cortana-powered experience, SDF only; questions should go to outlookcortanafb@microsoft.com'
            }
        </div>
    );
}

function renderSection(
    viewState: CortanaComposeViewState,
    section: CortanaComposeResult[],
    isSelectedSection: boolean
): JSX.Element[] {
    if (section) {
        return section.map((result, index) =>
            renderSearchResult(
                result,
                isSelectedSection &&
                    result.selectable &&
                    viewState.selectedIndexInSection === index,
                index.toString()
            )
        );
    }

    return [];
}

function renderSearchResult(
    result: CortanaComposeResult,
    isHighlighted: boolean,
    key: string
): JSX.Element {
    switch (result.type) {
        case CortanaComposeResultType.loading:
            return (
                <CortanaComposeResultComponent
                    title={strings.loadingSearchPopupText}
                    key={key}
                    showSpinner={true}
                    isHighlighted={isHighlighted}
                    result={result}
                />
            );
        case CortanaComposeResultType.noResults:
            return (
                <CortanaComposeResultComponent
                    title={strings.noResultsSearchPopupText}
                    key={key}
                    showSpinner={false}
                    isHighlighted={isHighlighted}
                    result={result}
                />
            );
        case CortanaComposeResultType.searching:
            return (
                <CortanaComposeResultComponent
                    title={strings.searchingSearchPopupText}
                    key={key}
                    showSpinner={true}
                    isHighlighted={isHighlighted}
                    result={result}
                />
            );
        case CortanaComposeResultType.doradoResult:
            return (
                <CortanaComposeResultComponent
                    title={result.resultObject.title}
                    key={key}
                    subtitle={result.resultObject.subTitle}
                    iconUrl={result.resultObject.imageUrl}
                    showSpinner={false}
                    result={result}
                    isHighlighted={isHighlighted}
                />
            );
        case CortanaComposeResultType.documentSuggestion:
            const icon = getIconForFile(result.resultObject.FileName);
            return (
                <CortanaComposeResultComponent
                    title={result.resultObject.Title}
                    key={key}
                    subtitle={result.resultObject.DateModified}
                    iconStyle={icon.small}
                    showSpinner={false}
                    isHighlighted={isHighlighted}
                    result={result}
                />
            );
        case CortanaComposeResultType.smartReplySuggestion:
            return (
                <CortanaComposeResultComponent
                    title={result.resultObject}
                    key={key}
                    showSpinner={false}
                    isHighlighted={isHighlighted}
                    result={result}
                    iconProps={{ iconName: 'Edit' }}
                />
            );
    }

    return <span />;
}

export function showInlineSearchPopup(
    targetPoint: IPoint,
    bufferZone: number,
    popupDiv: HTMLDivElement
): void {
    const viewState = cortanaComposeStore.viewState;
    ReactDOM.render(
        <InlineSearchPopup target={targetPoint} bufferZone={bufferZone} viewState={viewState} />,
        popupDiv
    );
}

export function disposeInlineSearchPopup(popupDiv: HTMLDivElement): void {
    ReactDOM.unmountComponentAtNode(popupDiv);
}
